import careerBanner from '@/../../public/storage/images/career-banner.webp';
import Banner from '@/components/Banner';
import NoResult from '@/components/NoResult';
import { Reveal, RevealGroup, RevealItem } from '@/components/Reveal';
import ShareDialog from '@/components/ShareDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import Typography from '@/components/ui/typography';
import Band from '@/Layouts/Band';
import SectionLayout from '@/Layouts/SectionLayout';
import { Job, PageProps } from '@/types';
import { yupResolver } from '@hookform/resolvers/yup';
import { router, usePage } from '@inertiajs/react';
import { Mail, MoveRight } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as yup from 'yup';

function FileValidator(attachedFile: File) {
  if (attachedFile) {
    // Check file type
    if (!attachedFile.type.toLowerCase().endsWith('pdf')) {
      return 'Only PDF files are allowed. Please select a PDF file.';
    }

    // Check file size
    if (attachedFile.size > 100000) {
      return 'File size exceeds the limit of 100KB. Please upload a smaller file.';
    }
  }
}

const CareerSchema = yup.object({
  job_id: yup.string().required('Job is required'),
  resume: yup
    .mixed<File>()
    .required('Please select a file to upload')
    .test('file-size', 'Invalid File', (value, context) => {
      const error = FileValidator(value);
      return error ? context.createError({ message: error }) : true;
    }),
});

type CareerFormData = yup.InferType<typeof CareerSchema>;

const Career = ({ jobs }: { jobs: Job[]; status: string }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [progress, setProgress] = useState(0);

  const { user } = usePage<PageProps>().props.auth;

  const form = useForm<CareerFormData>({
    resolver: yupResolver(CareerSchema),
    defaultValues: {
      job_id: '',
    },
  });

  // destructure form state to subscribe to proxy form state
  const { isSubmitting } = form.formState;

  const onValidData = async (data: CareerFormData) => {
    await new Promise<void>((resolve) =>
      router.post(route('apply-job'), data, {
        onProgress: (event) => {
          const percentage = event?.percentage ?? 0;

          // Calculate the progress rounded down to the nearest multiple of 10
          const roundedProgress = Math.floor(percentage / 10) * 10;

          // Only update state if the progress has changed
          if (roundedProgress !== progress) {
            setProgress(roundedProgress);
          }
        },
        onError: (errors) => {
          for (const field in errors) {
            const error = errors[field];

            // If the field is in the data object, set the error on the form
            // Otherwise, show the error as a toast
            if (Object.prototype.hasOwnProperty.call(data, field)) {
              form.setError(field as keyof typeof data, {
                type: 'server',
                message: error,
              });
            } else {
              toast.error(error);
            }
          }
        },
        onSuccess: ({ props }) => {
          if (props.status) {
            toast.success(props.status as string);
            window.dataLayer?.push({ event: 'job_application' });
            form.reset();
          }
        },
        onFinish: () => {
          setOpenDialog(false);
          resolve();
        },
      })
    );
  };

  return (
    <>
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className=" sm:max-w-106.25">
          <DialogHeader>
            <DialogTitle>Upload Resume</DialogTitle>
            <DialogDescription>
              Attach your resume to get started.
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onValidData)}
              className="flex flex-col gap-4"
            >
              <div className="grid w-full max-w-sm items-center gap-1.5">
                <FormField
                  control={form.control}
                  name="resume"
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormItem>
                      <FormLabel>Attach Resume</FormLabel>
                      <FormControl>
                        <Input
                          type="file"
                          {...field}
                          onChange={(e) => onChange(e.target.files?.[0])}
                        />
                      </FormControl>
                      <FormDescription>
                        File should be in .pdf format under 100 kb
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {progress > 0 && (
                  <progress value={progress} max="100">
                    {progress}%
                  </progress>
                )}
              </div>
              <Button type="submit" disabled={isSubmitting}>
                Submit
              </Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Banner
        image={careerBanner}
        width={1500}
        height={857}
        alt="Career"
        tagline="Challenge. Collaborate. Conquer."
        title="Careers at Samuthrika Academy"
        description="Samuthrika Academy welcomes skilled professionals who are
                passionate about teaching, training and helping learners build
                practical skills. Explore current openings and submit your
                application for relevant roles."
      >
        <ShareDialog
          url={window.location.href}
          title={'Careers - ' + import.meta.env.VITE_APP_NAME}
          content="Samuthrika Academy welcomes skilled professionals who are passionate about teaching, training and helping learners build practical skills. Explore current openings and submit your application for relevant roles."
        />
      </Banner>

      <Band tone="raised" pattern="lattice">
        <SectionLayout space="lg">
          {jobs.length > 0 ? (
            <>
              <Reveal className="flex max-w-2xl flex-col gap-3">
                <Typography.Eyebrow>
                  {jobs.length} {jobs.length === 1 ? 'opening' : 'openings'}
                </Typography.Eyebrow>
                <Typography.Display2 className="text-2xl md:text-3xl lg:text-4xl">
                  Roles we are hiring for
                </Typography.Display2>
                <Typography.Body className="text-muted-foreground">
                  Explore our current openings and see if there is a fit for
                  your skills and experience.
                </Typography.Body>
              </Reveal>

              {/* Cards carry the site's shell — border, card surface, lift on
                  hover — rather than the shadow-none/bg-none Card they had,
                  which rendered as text floating on the band. */}
              <RevealGroup className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {jobs.map((job) => (
                  <RevealItem key={job.title} className="flex">
                    <article className="flex h-full w-full flex-col items-start gap-4 rounded-xl border bg-card p-6 shadow-card transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-pop">
                      <div className="flex flex-col gap-1">
                        <Typography.Eyebrow>{job.job_code}</Typography.Eyebrow>
                        <Typography.Display3 className="text-xl md:text-xl lg:text-2xl">
                          {job.title}
                        </Typography.Display3>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <Badge variant="secondary">{job.type}</Badge>
                        <Badge variant="secondary">{job.location}</Badge>
                      </div>

                      <Typography.Subtle className="line-clamp-5">
                        {job.description}
                      </Typography.Subtle>

                      <div className="mt-auto flex flex-col items-start gap-2 pt-4">
                        <Button
                          onClick={() => {
                            if (!user) {
                              router.visit(route('login'));
                            } else {
                              setOpenDialog(true);
                              form.setValue('job_id', job.id.toString());
                            }
                          }}
                        >
                          Apply now
                          <MoveRight size={16} className="ml-2" />
                        </Button>
                        <Typography.Small>
                          Posted {new Date(job.posted_at).toDateString()}
                        </Typography.Small>
                      </div>
                    </article>
                  </RevealItem>
                ))}
              </RevealGroup>
            </>
          ) : (
            <NoResult kind="Job Openings" />
          )}
        </SectionLayout>
      </Band>

      <Band tone="ink" pattern="lattice" patternOpacity="medium">
        <SectionLayout space="lg">
          <Reveal className="flex flex-col items-start gap-5 md:items-center md:text-center">
            <Typography.Eyebrow>Nothing that fits?</Typography.Eyebrow>
            <Typography.Display2 className="max-w-2xl text-2xl md:text-3xl lg:text-4xl">
              Write to us anyway.
            </Typography.Display2>
            <Typography.Body className="max-w-xl text-ink-foreground/75">
              Our talent team reads every application. Tell us what you do and
              where you think you would fit.
            </Typography.Body>
            <Button asChild size="lg">
              <a
                href="mailto:career@samuthrika.academy?subject=Job Application"
                onClick={() => window.dataLayer?.push({ event: 'email_click' })}
                target="_blank"
                rel="noreferrer"
              >
                <Mail size={18} className="mr-2" />
                career@samuthrika.academy
              </a>
            </Button>
          </Reveal>
        </SectionLayout>
      </Band>
    </>
  );
};

export default Career;
